/**
 * TanStack Query hooks for billing reads and writes.
 *
 * Replaces the hand-rolled load/poll machinery that used to live in
 * BillingPage: caching, dedup, in-flight state and refetch-on-mount all come
 * from the query layer now, so the page only describes WHAT it is waiting for.
 *
 * The one genuinely billing-specific concern is the **mirror wait**. Every
 * write route mutates the subscription at Stripe and returns as soon as Stripe
 * acks; our billing_subscriptions row is only updated when the
 * customer.subscription.* webhook arrives. Invalidating on success therefore
 * refetches the PRE-change state and the page looks like nothing happened. So
 * a mutation hands `useSubscription` a predicate describing the change it
 * expects, and the query polls until the mirror satisfies it (or the deadline
 * passes). See MirrorWait below.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getActiveOrgEmail, getActiveTenantId } from '@papan/api-client'
import {
  billingApi,
  type CreditPurchase,
  type CreditsResponse,
  type SubscriptionResponse,
  type SubscriptionTermView,
} from './index.js'

/**
 * Identity component of tenant-scoped cache keys — same rationale as
 * `fileKeys` in features/files: switching tenant or org identity changes what
 * the server returns, and without identity in the key the previous tenant's
 * billing data would be served for the whole staleTime window. A tenant seeing
 * another tenant's plan and invoices is the worst possible version of that bug.
 */
function identityKey(): readonly [tenantId: string | null, orgEmail: string | null] {
  return [getActiveTenantId(), getActiveOrgEmail()] as const
}

export const billingKeys = {
  all: ['billing'] as const,
  /** Global catalog — NOT identity-scoped: /plans reads no tenant state. */
  plans: () => [...billingKeys.all, 'plans'] as const,
  subscription: () => [...billingKeys.all, 'subscription', ...identityKey()] as const,
  invoices: () => [...billingKeys.all, 'invoices', ...identityKey()] as const,
  paymentMethod: () => [...billingKeys.all, 'payment-method', ...identityKey()] as const,
  /** History length is part of the key: "show more" is a different read, and
   *  sharing a key would serve the 10-row payload for the 50-row request. */
  credits: (limit: number) => [...billingKeys.all, 'credits', limit, ...identityKey()] as const,
  /** Identity-scoped: it reads THIS tenant's payment state. */
  manualStatement: () => [...billingKeys.all, 'manual-statement', ...identityKey()] as const,
  /** Identity-scoped: storage used is per-tenant. */
  storage: () => [...billingKeys.all, 'storage', ...identityKey()] as const,
  /** Identity-scoped: the org's own subscription-term history (Task 19). */
  subscriptionTerms: () => [...billingKeys.all, 'subscription-terms', ...identityKey()] as const,
}

/** How often to re-read while waiting for a webhook to land. */
const MIRROR_POLL_INTERVAL_MS = 1500
/** How long to keep waiting before giving up and showing whatever we have. */
export const MIRROR_POLL_TIMEOUT_MS = 15_000

/**
 * A change we've made at Stripe and are waiting for our own mirror to reflect.
 * `done` is the observable evidence it landed; `deadline` bounds the wait so a
 * webhook that never arrives degrades to the normal page rather than polling
 * forever.
 */
export interface MirrorWait {
  done: (sub: SubscriptionResponse) => boolean
  deadline: number
}

/** Build a MirrorWait with the standard timeout. */
export function mirrorWait(done: (sub: SubscriptionResponse) => boolean): MirrorWait {
  return { done, deadline: Date.now() + MIRROR_POLL_TIMEOUT_MS }
}

/**
 * What a manually-billed customer owes, and until when.
 *
 * Fetched unconditionally rather than behind `planAuthority === 'manual'`: the
 * authority arrives on the plans query, so gating this on it would serialise
 * two round trips and make the statement pop in after the rest of the page had
 * settled. The route answers `{ applicable: false }` cheaply on a
 * provider-billed deployment, which is one read either way.
 *
 * `staleTime` matches the subscription's for the same reason — this is
 * money-facing, and the page is usually opened because something about it just
 * changed.
 */
export function useManualStatement(enabled: boolean) {
  return useQuery({
    queryKey: billingKeys.manualStatement(),
    queryFn: () => billingApi.getManualStatement(),
    enabled,
    staleTime: 15_000,
  })
}

/**
 * The tenant's effective plan, seats and usage.
 *
 * Pass `awaiting` after a write to poll until the webhook mirror catches up.
 * Polling stops as soon as the predicate holds or the deadline passes — it is
 * never an unconditional interval, so an idle billing page makes no traffic.
 */
export function useSubscription(awaiting?: MirrorWait | null) {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => billingApi.getSubscription(),
    // Shorter than the 30s app default: this is money-facing, and the page is
    // usually opened precisely because something about it just changed.
    staleTime: 15_000,
    refetchInterval: (query) => {
      if (!awaiting || Date.now() > awaiting.deadline) return false
      const data = query.state.data
      if (data && awaiting.done(data)) return false
      return MIRROR_POLL_INTERVAL_MS
    },
  })
}

/** The active plan catalog plus whether write routes exist on this install. */
export function usePlans() {
  return useQuery({
    queryKey: billingKeys.plans(),
    queryFn: () => billingApi.listPlans(),
    // The catalog changes on deploys, not during a session.
    staleTime: 5 * 60_000,
  })
}

/**
 * Billing history. Owner/admin-only server-side — a member hitting the 403 is
 * not an error worth surfacing, so it resolves to an empty list and the page
 * simply renders without the history section.
 */
export function useInvoices(enabled: boolean) {
  return useQuery({
    queryKey: billingKeys.invoices(),
    queryFn: () => billingApi.listInvoices().catch(() => ({ invoices: [] })),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * The default card on file. Owner/admin-only server-side and read live from
 * Stripe, so — like invoices — a member's 403 (or any transient Stripe hiccup)
 * resolves to `null` rather than surfacing an error; the page just omits the
 * card row. Longer staleTime than the subscription: a card changes rarely and
 * only via the portal round-trip, which invalidates on return anyway.
 */
export function usePaymentMethod(enabled: boolean) {
  return useQuery({
    queryKey: billingKeys.paymentMethod(),
    queryFn: () => billingApi.getPaymentMethod().catch(() => ({ paymentMethod: null })),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * A credit top-up we've paid for and are waiting for the webhook to apply.
 *
 * Same problem as the subscription mirror, one step worse: Stripe returns the
 * customer to this page the instant they pay, but the balance only moves when
 * `checkout.session.completed` reaches us. Without a wait the customer lands on
 * the page they just spent money on and sees the OLD number — which reads as a
 * failed payment, and the natural response is to pay again.
 *
 * `atLeast` is the balance we recorded before redirecting; anything strictly
 * above it means the credit landed. Recorded in sessionStorage rather than
 * component state because the browser leaves for Stripe in between.
 */
export interface CreditWait {
  atLeast: number
  deadline: number
}

/** Where the pre-checkout balance is parked across the Stripe round trip. */
const CREDIT_PENDING_KEY = 'papan:credits:pending'

/**
 * Remember the balance before handing off to Stripe.
 *
 * Best-effort: a browser with sessionStorage disabled (or a return in a
 * different tab) simply gets no wait, which degrades to the ordinary refetch
 * plus the "may take a moment" note — never to a wrong number.
 */
export function stashCreditBalance(balanceBillingMicro: number): void {
  try {
    sessionStorage.setItem(CREDIT_PENDING_KEY, String(balanceBillingMicro))
  } catch { /* storage unavailable — the wait is an affordance, not a guarantee */ }
}

/** Consume the stashed balance, if this tab is the one that started checkout. */
export function takeCreditWait(): CreditWait | null {
  try {
    const raw = sessionStorage.getItem(CREDIT_PENDING_KEY)
    sessionStorage.removeItem(CREDIT_PENDING_KEY)
    if (raw === null) return null
    const atLeast = Number(raw)
    if (!Number.isFinite(atLeast)) return null
    return { atLeast, deadline: Date.now() + MIRROR_POLL_TIMEOUT_MS }
  } catch {
    return null
  }
}

/**
 * Usage credits. Any member may read this — the balance explains a 402 they
 * cannot otherwise act on.
 *
 * `limit` re-keys the cache rather than paging in place: the endpoint returns
 * balance + settings + packs alongside the history, so a "show more" is a
 * cheap re-read of a small payload, not worth an infinite-query machine.
 */
export function useCredits(limit: number, awaiting?: CreditWait | null, enabled = true) {
  return useQuery({
    queryKey: billingKeys.credits(limit),
    queryFn: () => billingApi.getCredits({ limit }),
    enabled,
    staleTime: 15_000,
    refetchInterval: (query) => {
      if (!awaiting || Date.now() > awaiting.deadline) return false
      const data = query.state.data
      if (data && data.balanceBillingMicro > awaiting.atLeast) return false
      return MIRROR_POLL_INTERVAL_MS
    },
  })
}

/**
 * Storage used vs quota for the Billing key-info card (manual mode).
 *
 * Enabled only when a tenant id is resolvable — a personal drive still has one.
 * Its own read rather than a field on the subscription payload: the used figure
 * needs a live drive walk the subscription route deliberately avoids, and a
 * separate query lets the hero paint immediately and the storage stat fill in.
 */
export function useStorageComposition(enabled = true) {
  const tenantId = getActiveTenantId()
  return useQuery({
    queryKey: billingKeys.storage(),
    queryFn: () => billingApi.getStorageComposition(tenantId as string),
    enabled: enabled && !!tenantId,
    staleTime: 30_000,
  })
}

/**
 * The org's own subscription-term history — read-only timeline (Task 19).
 *
 * `manage_billing:read`, same gate `useSubscription`/`useManualStatement` sit
 * behind. `enabled` exists for a caller that wants to withhold the fetch while
 * its own capability check is still resolving (the loading-vs-denial
 * discipline — see `BillingPage`'s `subscriptionTermsLoading` prop); every
 * existing host already only mounts `BillingPage` once that check has
 * resolved to granted, so `enabled: true` is the common case.
 *
 * Errors resolve to an empty list rather than surfacing, like `useInvoices` —
 * this is a read-only auxiliary timeline, not a reason the whole page should
 * show an error state.
 */
export function useSubscriptionTerms(enabled: boolean) {
  return useQuery({
    queryKey: billingKeys.subscriptionTerms(),
    queryFn: () => billingApi.listSubscriptionTerms().catch(() => ({ terms: [] as SubscriptionTermView[] })),
    enabled,
    staleTime: 60_000,
  })
}

/** Start credit Checkout. Redirect-and-leave, like `useSubscribe`. */
export function useBuyCredits() {
  return useMutation({
    mutationFn: (purchase: CreditPurchase) => billingApi.buyCredits(purchase),
  })
}

/** Toggle credits / set the low-balance threshold. */
export function useUpdateCreditSettings() {
  const invalidate = useInvalidateBilling()
  return useMutation({
    mutationFn: (patch: { enabled?: boolean; lowBalanceThresholdBillingMicro?: number | null }) =>
      billingApi.updateCreditSettings(patch),
    onSuccess: invalidate,
  })
}

export type { CreditsResponse }

/** Invalidate every billing read for the active identity. */
function useInvalidateBilling() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: billingKeys.all })
  }
}

export function useChangePlan() {
  const invalidate = useInvalidateBilling()
  return useMutation({
    mutationFn: (planId: string) => billingApi.changePlan(planId),
    onSuccess: invalidate,
  })
}

export function useCancelSubscription() {
  const invalidate = useInvalidateBilling()
  return useMutation({
    mutationFn: () => billingApi.cancel(),
    onSuccess: invalidate,
  })
}

/**
 * Start Checkout. Returns the Stripe-hosted URL; the caller redirects. No
 * invalidation — the browser is leaving, and the return trip re-reads anyway.
 */
export function useSubscribe() {
  return useMutation({
    mutationFn: (planId: string) => billingApi.subscribe(planId),
  })
}

/** Open the Stripe Billing Portal. Same redirect-and-leave shape as above. */
export function useOpenPortal() {
  return useMutation({
    mutationFn: () => billingApi.openPortal(),
  })
}
